import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  RefreshCw,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  PieChart,
  Building2,
} from 'lucide-react';
import { getBillingAnalytics, exportBillingToExcel, BillingAnalytics } from '@/services/analyticsService';

export const BillingAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<BillingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBillingAnalytics();
      setData(response);
    } catch (err) {
      setError('Erreur lors du chargement des analyses de facturation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportBillingToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `billing-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: <CheckCircle size={14} /> },
      TRIAL: { label: 'Essai', color: 'bg-blue-100 text-blue-800', icon: <Clock size={14} /> },
      PAST_DUE: { label: 'En retard', color: 'bg-yellow-100 text-yellow-800', icon: <AlertCircle size={14} /> },
      CANCELLED: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: <XCircle size={14} /> },
      SUSPENDED: { label: 'Suspendu', color: 'bg-red-100 text-red-800', icon: <AlertCircle size={14} /> },
    };
    return labels[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: null };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchData}
            className="ml-auto px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facturation & Abonnements</h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyse des revenus et abonnements de la plateforme
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Export...' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Revenue Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* MRR */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">MRR</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(data?.mrr || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-white" size={24} />
            </div>
          </div>
          <div className={`mt-4 text-sm flex items-center gap-1 ${
            (data?.revenueGrowth || 0) >= 0 ? 'text-green-100' : 'text-red-200'
          }`}>
            {(data?.revenueGrowth || 0) >= 0 ? (
              <TrendingUp size={14} />
            ) : (
              <TrendingDown size={14} />
            )}
            {formatPercent(data?.revenueGrowth || 0)} vs mois dernier
          </div>
        </div>

        {/* ARR */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">ARR</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(data?.arr || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <DollarSign className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-blue-100">
            Revenu annualisé
          </p>
        </div>

        {/* ARPA */}
        <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">ARPA</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(data?.arpa || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Building2 className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-purple-100">
            Revenu moyen par compte
          </p>
        </div>

        {/* NRR */}
        <div className={`bg-gradient-to-br ${(data?.nrr || 100) >= 100 ? 'from-teal-500 to-cyan-600' : 'from-orange-500 to-red-600'} rounded-xl p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">NRR</p>
              <p className="text-3xl font-bold mt-1">
                {(data?.nrr || 100).toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              {(data?.nrr || 100) >= 100 ? (
                <TrendingUp className="text-white" size={24} />
              ) : (
                <TrendingDown className="text-white" size={24} />
              )}
            </div>
          </div>
          <p className="mt-4 text-sm text-white/80">
            Net Revenue Retention
          </p>
        </div>
      </div>

      {/* Revenue Cards - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Net New MRR */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Net New MRR</p>
              <p className={`text-2xl font-bold mt-1 ${(data?.netNewMrr || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(data?.netNewMrr || 0) >= 0 ? '+' : ''}{formatCurrency(data?.netNewMrr || 0)}
              </p>
            </div>
            <div className={`w-10 h-10 ${(data?.netNewMrr || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
              {(data?.netNewMrr || 0) >= 0 ? (
                <TrendingUp className="text-green-600" size={20} />
              ) : (
                <TrendingDown className="text-red-600" size={20} />
              )}
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Nouveau - Churn ce mois
          </p>
        </div>

        {/* Churn Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Taux de Churn</p>
              <p className={`text-2xl font-bold mt-1 ${(data?.churnRate || 0) <= 5 ? 'text-green-600' : (data?.churnRate || 0) <= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                {(data?.churnRate || 0).toFixed(1)}%
              </p>
            </div>
            <div className={`w-10 h-10 ${(data?.churnRate || 0) <= 5 ? 'bg-green-100' : 'bg-red-100'} rounded-lg flex items-center justify-center`}>
              <XCircle className={(data?.churnRate || 0) <= 5 ? 'text-green-600' : 'text-red-600'} size={20} />
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Désabonnements ce mois
          </p>
        </div>

        {/* Trial Conversion */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Conversion Essai</p>
              <p className={`text-2xl font-bold mt-1 ${(data?.trialConversionRate || 0) >= 20 ? 'text-green-600' : 'text-yellow-600'}`}>
                {(data?.trialConversionRate || 0).toFixed(1)}%
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-blue-600" size={20} />
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Essai vers payant
          </p>
        </div>

        {/* Outstanding */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Impayés</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(data?.outstandingAmount || 0)}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-red-600" size={20} />
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {data?.outstandingCount || 0} factures en attente
          </p>
        </div>
      </div>

      {/* Subscription Status & Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Statut des abonnements</h3>
            <span className="text-sm text-gray-500">
              {data?.totalSubscriptions || 0} total
            </span>
          </div>
          <div className="space-y-4">
            {data?.statusBreakdown && Object.entries(data.statusBreakdown).map(([status, count]) => {
              const statusInfo = getStatusLabel(status);
              const percentage = data.totalSubscriptions
                ? (count / data.totalSubscriptions) * 100
                : 0;
              return (
                <div key={status} className="flex items-center gap-4">
                  <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    {statusInfo.icon}
                    {statusInfo.label}
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          status === 'ACTIVE' ? 'bg-green-500' :
                          status === 'TRIAL' ? 'bg-blue-500' :
                          status === 'PAST_DUE' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Répartition par plan</h3>
            <PieChart className="text-gray-400" size={20} />
          </div>
          {data?.planDistribution && data.planDistribution.length > 0 ? (
            <div className="space-y-4">
              {data.planDistribution.map((plan, index) => {
                const colors = ['bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-blue-500'];
                return (
                  <div key={plan.planId} className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`} />
                    <span className="flex-1 text-gray-700">{plan.planName}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {plan.count} abonnés
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-gray-500">Aucun abonnement</p>
            </div>
          )}
        </div>
      </div>

      {/* LTV & Advanced Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LTV */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">Customer LTV</p>
              <p className="text-3xl font-bold mt-1">
                {formatCurrency(data?.ltv || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <DollarSign className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-amber-100">
            Valeur vie client moyenne
          </p>
        </div>

        {/* LTV:CAC Ratio */}
        <div className={`bg-gradient-to-br ${(data?.ltvCacRatio || 0) >= 3 ? 'from-green-500 to-emerald-600' : (data?.ltvCacRatio || 0) >= 2 ? 'from-yellow-500 to-amber-600' : 'from-red-500 to-rose-600'} rounded-xl p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">LTV:CAC Ratio</p>
              <p className="text-3xl font-bold mt-1">
                {(data?.ltvCacRatio || 0).toFixed(1)}x
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <PieChart className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-white/80">
            {(data?.ltvCacRatio || 0) >= 3 ? 'Excellent' : (data?.ltvCacRatio || 0) >= 2 ? 'Bon' : 'A améliorer'} (cible: 3x+)
          </p>
        </div>

        {/* Customer Lifetime */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Durée vie client</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {(data?.avgCustomerLifetimeMonths || 0).toFixed(1)} mois
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Clock className="text-indigo-600" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Basé sur taux de churn actuel
          </p>
        </div>
      </div>

      {/* MRR Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Décomposition MRR</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-100">
            <p className="text-sm text-green-700 font-medium">New MRR</p>
            <p className="text-xl font-bold text-green-800 mt-1">
              +{formatCurrency(data?.newMrr || 0)}
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-700 font-medium">Expansion MRR</p>
            <p className="text-xl font-bold text-blue-800 mt-1">
              +{formatCurrency(data?.expansionMrr || 0)}
            </p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <p className="text-sm text-yellow-700 font-medium">Contraction MRR</p>
            <p className="text-xl font-bold text-yellow-800 mt-1">
              -{formatCurrency(data?.contractionMrr || 0)}
            </p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-100">
            <p className="text-sm text-red-700 font-medium">Churn MRR</p>
            <p className="text-xl font-bold text-red-800 mt-1">
              -{formatCurrency(data?.churnMrr || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* TVA Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">TVA collectée ce mois</h3>
            <p className="text-sm text-gray-500 mt-1">
              Taux standard: 20% (Maroc)
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(data?.tvaCollectedThisMonth || 0)}
            </p>
            <p className="text-sm text-gray-500 mt-1">TVA (20%)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingAnalyticsPage;
